import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useCurrency } from "../../../_helper/CurrencyContext";
import { usePharmacy } from "../../../_helper/PharmacyProvider";
import { toMoneyNumber } from "../../../utils/financeEngine";
import { getGeneralServiceMaster, getLabTestMaster } from "../../PharmacySales/SalesService";
import {
	buildBillingContextFields,
	buildBillingSummary,
	buildPharmacyItemsFromBilling,
	buildPharmacyTotalsFromBilling,
	buildSectionLineBillingFieldsMap,
	buildTreatmentBillingPayloadExtras,
	buildTreatmentBillingSummary,
	calculateInvestigationBillingTotals,
	calculateServicesBillingTotals,
	convertBillingAmount,
	convertLineAmount,
	createEmptyInvestigationRow,
	createEmptySectionTaxConfig,
	createEmptyServiceRow,
	enrichInvestigationsWithMaster,
	enrichServiceItemsWithMaster,
	generalServiceFromMaster,
	getBillingBaseCurrency,
	getEffectiveSectionTaxConfig,
	getPrescriptionBillingFlags,
	getInvestigationUnitPrice,
	getSectionDiscountFromSummary,
	getSectionTaxConfig,
	getServiceUnitPrice,
	getTreatmentPackagePrice,
	hasPatientAccountBilling,
	initializePatientAccountBillingItems,
	investigationFromMaster,
	normalizeCurrencyCode,
	parseGeneralServiceMasterResponse,
	parseLabMasterResponse,
	recalculateInvestigationLine,
	recalculateServiceLine,
	syncGstTaxConfig,
	getTaxConfigFromBillingSummary,
} from "../../PharmacySales/NewSales/utils/billingUtils";
import {
	buildTreatmentAdditionalServices,
	buildTreatmentInvestigationItems,
	buildTreatmentPackageDetails,
	buildTreatmentPackagePaymentHistory,
	withPharmacyItemSaleDate,
	isTreatmentPackageInvoice,
	normalizePatientAccountItem,
} from "../utils/patientAccountUtils";

const clampTaxPercent = (value) => {
	const num = toMoneyNumber(value);
	if (num < 0) return 0;
	if (num > 100) return 100;
	return num;
};

export const useCollectPaymentBilling = ({ invoice, isOpen, paymentCurrency }) => {
	const { t } = useTranslation();
	const { pharmacyPOS } = usePharmacy();
	const { allowedCurrencies, convertCurrency } = useCurrency();

	const posTaxType = pharmacyPOS?.taxType || "gst";
	const pharmacyStateCode = pharmacyPOS?.stateCode || "";
	const patientStateCode = invoice?.patient?.state_code || invoice?.patient?.stateCode || "";
	const pharmacyCurrency = normalizeCurrencyCode(
		pharmacyPOS?.currency,
		allowedCurrencies?.[0],
		allowedCurrencies
	);
	const defaultCurrency = normalizeCurrencyCode(
		invoice?.appointment_invoice?.currency ||
			invoice?.consultation?.currency ||
			invoice?.billing?.summary?.currency ||
			invoice?.billing?.payment?.base_currency ||
			pharmacyPOS?.currency,
		pharmacyCurrency,
		allowedCurrencies
	);

	const [loading, setLoading] = useState(false);
	const [serviceItems, setServiceItems] = useState([]);
	const [investigationItems, setInvestigationItems] = useState([]);
	const [servicesDiscountType, setServicesDiscountType] = useState("Percentage");
	const [servicesDiscountValue, setServicesDiscountValue] = useState(0);
	const [servicesTaxConfig, setServicesTaxConfig] = useState(() =>
		createEmptySectionTaxConfig(posTaxType)
	);
	const [investigationsDiscountType, setInvestigationsDiscountType] = useState("Percentage");
	const [investigationsDiscountValue, setInvestigationsDiscountValue] = useState(0);
	const [investigationsTaxConfig, setInvestigationsTaxConfig] = useState(() =>
		createEmptySectionTaxConfig(posTaxType)
	);
	const [labMasterList, setLabMasterList] = useState([]);
	const [generalMasterList, setGeneralMasterList] = useState([]);
	const [sectionOpen, setSectionOpen] = useState({
		services: true,
		investigations: true,
		medicines: true,
	});

	const normalizedInvoice = useMemo(
		() => (invoice ? normalizePatientAccountItem(invoice) : null),
		[invoice]
	);

	const isTreatmentPackage = useMemo(
		() => isTreatmentPackageInvoice(invoice),
		[invoice]
	);

	const treatmentDetails = useMemo(
		() => (isTreatmentPackage ? buildTreatmentPackageDetails(invoice) : null),
		[invoice, isTreatmentPackage]
	);

	const treatmentPaymentHistory = useMemo(
		() => (isTreatmentPackage ? buildTreatmentPackagePaymentHistory(invoice) : []),
		[invoice, isTreatmentPackage]
	);

	const baseCurrency = normalizedInvoice?.currency || defaultCurrency;
	const billingCurrency = baseCurrency;
	const resolvedPaymentCurrency = paymentCurrency || billingCurrency;
	const displayCurrency = billingCurrency;

	const isSameState = useMemo(
		() =>
			!patientStateCode ||
			!pharmacyStateCode ||
			pharmacyStateCode === patientStateCode,
		[patientStateCode, pharmacyStateCode]
	);

	const billingFlags = useMemo(
		() => getPrescriptionBillingFlags(invoice),
		[invoice]
	);

	const pharmacyItems = useMemo(
		() =>
			hasPatientAccountBilling(invoice)
				? buildPharmacyItemsFromBilling(invoice?.billing?.pharmacy_items)
				: [],
		[invoice]
	);

	const pharmacyTotals = useMemo(
		() =>
			hasPatientAccountBilling(invoice)
				? buildPharmacyTotalsFromBilling(invoice?.billing)
				: {
						subtotal: 0,
						netTotal: 0,
						discount: 0,
						tax: 0,
						CGST: 0,
						SGST: 0,
						IGST: 0,
						VAT: 0,
					},
		[invoice]
	);

	const servicesBillingTotals = useMemo(
		() =>
			calculateServicesBillingTotals({
				serviceItems,
				discountType: servicesDiscountType,
				discountValue: servicesDiscountValue,
				taxConfig: getEffectiveSectionTaxConfig(
					servicesTaxConfig,
					billingFlags.showServicesTax,
					posTaxType
				),
				isSameState,
				targetCurrency: billingCurrency,
				convertCurrency,
				pharmacyCurrency: defaultCurrency,
				allowedCurrencies,
			}),
		[
			allowedCurrencies,
			billingCurrency,
			convertCurrency,
			defaultCurrency,
			isSameState,
			serviceItems,
			servicesDiscountType,
			servicesDiscountValue,
			servicesTaxConfig,
			billingFlags.showServicesTax,
			posTaxType,
		]
	);

	const packagePrice = useMemo(
		() =>
			isTreatmentPackage
				? getTreatmentPackagePrice(invoice) ||
					toMoneyNumber(treatmentDetails?.packagePrice ?? 0)
				: 0,
		[invoice, isTreatmentPackage, treatmentDetails?.packagePrice]
	);

	const investigationsBillingTotals = useMemo(
		() =>
			calculateInvestigationBillingTotals({
				investigationItems,
				discountType: investigationsDiscountType,
				discountValue: investigationsDiscountValue,
				taxConfig: getEffectiveSectionTaxConfig(
					investigationsTaxConfig,
					isTreatmentPackage || billingFlags.showLabTax,
					posTaxType
				),
				isSameState,
				targetCurrency: billingCurrency,
				convertCurrency,
				pharmacyCurrency: defaultCurrency,
				allowedCurrencies,
			}),
		[
			allowedCurrencies,
			billingCurrency,
			convertCurrency,
			defaultCurrency,
			investigationItems,
			investigationsDiscountType,
			investigationsDiscountValue,
			investigationsTaxConfig,
			billingFlags.showLabTax,
			isTreatmentPackage,
			isSameState,
			posTaxType,
		]
	);

	const billingSummary = useMemo(() => {
		const showLab = isTreatmentPackage || billingFlags.showLabSection;
		const baseSummary = buildBillingSummary({
			serviceItems,
			servicesBilling: servicesBillingTotals,
			investigationItems: showLab ? investigationItems : [],
			investigationsBilling: showLab ? investigationsBillingTotals : null,
			pharmacyItems,
			pharmacyTotals,
			pharmacyDiscountType: invoice?.billing?.summary?.pharmacy_discount_type || "Percentage",
			pharmacyDiscountValue: invoice?.billing?.summary?.pharmacy_discount_value ?? 0,
			targetCurrency: billingCurrency,
			convertCurrency,
			pharmacyCurrency: defaultCurrency,
			allowedCurrencies,
		});

		if (!isTreatmentPackage) return baseSummary;

		return buildTreatmentBillingSummary({
			baseSummary,
			prescription: invoice,
			serviceItems,
			packagePrice,
			targetCurrency: billingCurrency,
			convertCurrency,
			pharmacyCurrency: defaultCurrency,
			allowedCurrencies,
		});
	}, [
		allowedCurrencies,
		billingCurrency,
		convertCurrency,
		defaultCurrency,
		investigationItems,
		investigationsBillingTotals,
		billingFlags.showLabSection,
		invoice,
		invoice?.billing?.summary?.pharmacy_discount_type,
		invoice?.billing?.summary?.pharmacy_discount_value,
		isTreatmentPackage,
		packagePrice,
		pharmacyItems,
		pharmacyTotals,
		serviceItems,
		servicesBillingTotals,
	]);

	const basePaidSoFar = normalizedInvoice?.paid_amount ?? 0;
	const baseDueAmount = Math.max(
		0,
		toMoneyNumber(normalizedInvoice?.due_amount ?? 0)
	);
	const grandTotal = isTreatmentPackage
		? toMoneyNumber(
				billingSummary.grand_total ??
					treatmentDetails?.totalPayable ??
					normalizedInvoice?.invoice_amount ??
					invoice?.appointment_invoice?.net_total ??
					packagePrice +
						(billingSummary.services_total ?? 0) +
						(billingSummary.investigation_total ?? 0)
			)
		: billingSummary.grand_total ?? 0;
	const paidSoFar = isTreatmentPackage
		? toMoneyNumber(treatmentDetails?.totalCollected ?? basePaidSoFar)
		: basePaidSoFar;
	const dueAmount = useMemo(() => {
		if (isTreatmentPackage) {
			if (baseDueAmount > 0) return baseDueAmount;
			return Math.max(0, toMoneyNumber(grandTotal - paidSoFar));
		}
		if (baseDueAmount > 0) return baseDueAmount;
		return Math.max(0, toMoneyNumber(grandTotal - paidSoFar));
	}, [baseDueAmount, grandTotal, isTreatmentPackage, paidSoFar]);

	const dueAmountPayment = useMemo(() => {
		if (resolvedPaymentCurrency === billingCurrency) return dueAmount;
		return convertBillingAmount(
			dueAmount,
			billingCurrency,
			resolvedPaymentCurrency,
			convertCurrency,
			defaultCurrency,
			allowedCurrencies
		);
	}, [
		allowedCurrencies,
		billingCurrency,
		convertCurrency,
		defaultCurrency,
		dueAmount,
		resolvedPaymentCurrency,
	]);

	const fetchLabMasterList = useCallback(async () => {
		try {
			const response = await getLabTestMaster({
				clinic_id: pharmacyPOS?.hospital_id,
				search: "",
			});
			return parseLabMasterResponse(response);
		} catch (error) {
			console.error("Error loading lab tests:", error);
			return [];
		}
	}, [pharmacyPOS?.hospital_id]);

	const fetchGeneralMasterList = useCallback(async () => {
		try {
			const response = await getGeneralServiceMaster({
				clinic_id: pharmacyPOS?.hospital_id,
				search: "",
			});
			return parseGeneralServiceMasterResponse(response);
		} catch (error) {
			console.error("Error loading general services:", error);
			return [];
		}
	}, [pharmacyPOS?.hospital_id]);

	const initializeBilling = useCallback(async () => {
		if (!invoice) return;
		setLoading(true);
		try {
			const [labMaster, generalMaster] = await Promise.all([
				fetchLabMasterList(),
				fetchGeneralMasterList(),
			]);
			setLabMasterList(labMaster);
			setGeneralMasterList(generalMaster);

			const { serviceItems: initialServices, investigationItems: initialInvestigations } =
				isTreatmentPackage
					? {
							serviceItems: buildTreatmentAdditionalServices(invoice, {
								billableOnly: true,
								excludePharmacyAndLab: true,
								preferConsultationSource: true,
							}).map((item) => ({
								...item,
								isConsultation: false,
								isPrescribed: false,
								isNewRow: false,
								source: "added",
							})),
							investigationItems: buildTreatmentInvestigationItems(invoice).map((item) => ({
								...item,
								isPrescribed: false,
								isNewRow: false,
								source: "added",
							})),
						}
					: initializePatientAccountBillingItems(
							invoice,
							defaultCurrency,
							allowedCurrencies
						);

			setServiceItems(
				enrichServiceItemsWithMaster(
					initialServices,
					generalMaster,
					defaultCurrency,
					allowedCurrencies
				)
			);
			setInvestigationItems(
				enrichInvestigationsWithMaster(
					initialInvestigations,
					labMaster,
					defaultCurrency,
					allowedCurrencies
				)
			);

			const summary = invoice?.billing?.summary || {};
			const servicesDiscount = getSectionDiscountFromSummary(summary, "services");
			const investigationsDiscount = getSectionDiscountFromSummary(summary, "investigation");

			setServicesDiscountType(servicesDiscount.discountType);
			setServicesDiscountValue(servicesDiscount.discountValue);
			setInvestigationsDiscountType(investigationsDiscount.discountType);
			setInvestigationsDiscountValue(investigationsDiscount.discountValue);
			setServicesTaxConfig(
				hasPatientAccountBilling(invoice)
					? getTaxConfigFromBillingSummary(summary, "services", posTaxType)
					: getSectionTaxConfig(invoice, posTaxType)
			);
			setInvestigationsTaxConfig(
				hasPatientAccountBilling(invoice)
					? getTaxConfigFromBillingSummary(summary, "investigation", posTaxType)
					: createEmptySectionTaxConfig(posTaxType)
			);
		} catch (error) {
			console.error("Error initializing collect payment billing:", error);
			toast.error(t("Failed to load billing details. Please try again."));
		} finally {
			setLoading(false);
		}
	}, [
		allowedCurrencies,
		defaultCurrency,
		fetchGeneralMasterList,
		fetchLabMasterList,
		invoice,
		isTreatmentPackage,
		posTaxType,
		t,
	]);

	useEffect(() => {
		if (isOpen && invoice?._id) {
			initializeBilling();
		}
	}, [initializeBilling, invoice?._id, isOpen]);

	const toggleSection = useCallback((key) => {
		setSectionOpen((prev) => ({ ...prev, [key]: !prev[key] }));
	}, []);

	const updateServiceAmount = useCallback((id, amount) => {
		setServiceItems((prev) =>
			prev.map((item) => {
				if (item.id !== id) return item;
				const quantity = Math.max(1, Number(item.quantity) || 1);
				const lineAmount = toMoneyNumber(amount);
				return {
					...item,
					amount: lineAmount,
					unit_price: toMoneyNumber(lineAmount / quantity),
				};
			})
		);
	}, []);

	const updateServiceQuantity = useCallback((id, quantity) => {
		setServiceItems((prev) =>
			prev.map((item) =>
				item.id === id ? { ...item, ...recalculateServiceLine(item, quantity) } : item
			)
		);
	}, []);

	const updateInvestigationAmount = useCallback((id, amount) => {
		setInvestigationItems((prev) =>
			prev.map((item) => {
				if (item.id !== id) return item;
				const quantity = Math.max(1, Number(item.quantity) || 1);
				const lineAmount = toMoneyNumber(amount);
				return {
					...item,
					amount: lineAmount,
					unit_price: toMoneyNumber(lineAmount / quantity),
				};
			})
		);
	}, []);

	const updateInvestigationQuantity = useCallback((id, quantity) => {
		setInvestigationItems((prev) =>
			prev.map((item) =>
				item.id === id ? { ...item, ...recalculateInvestigationLine(item, quantity) } : item
			)
		);
	}, []);

	const removeServiceItem = useCallback((id) => {
		setServiceItems((prev) => {
			const target = prev.find((item) => item.id === id);
			if (!target || target.isConsultation || (target.isPrescribed && !target.isNewRow)) {
				return prev;
			}
			return prev.filter((item) => item.id !== id);
		});
	}, []);

	const removeInvestigation = useCallback((id) => {
		setInvestigationItems((prev) => prev.filter((item) => item.id !== id));
	}, []);

	const addServiceRow = useCallback(() => {
		setServiceItems((prev) => [...prev, createEmptyServiceRow(defaultCurrency)]);
	}, [defaultCurrency]);

	const addInvestigationRow = useCallback(() => {
		setInvestigationItems((prev) => [...prev, createEmptyInvestigationRow(defaultCurrency)]);
	}, [defaultCurrency]);

	const selectServiceForRow = useCallback(
		(rowId, serviceId) => {
			if (!serviceId) {
				setServiceItems((prev) =>
					prev.map((item) =>
						item.id === rowId
							? {
									...item,
									service_id: "",
									service_name: "",
									quantity: 1,
									unit_price: 0,
									amount: 0,
									isNewRow: true,
								}
							: item
					)
				);
				return;
			}

			const master = generalMasterList.find(
				(item) => String(item.service_id || item._id) === String(serviceId)
			);
			if (!master) return;

			const candidate = generalServiceFromMaster(
				master,
				defaultCurrency,
				allowedCurrencies
			);

			setServiceItems((prev) => {
				const duplicate = prev.some(
					(item) =>
						item.id !== rowId &&
						String(item.service_id) === String(candidate.service_id)
				);
				if (duplicate) {
					toast.info(t("Service already added."));
					return prev;
				}
				return prev.map((item) =>
					item.id === rowId
						? { ...item, ...candidate, id: item.id, isNewRow: !candidate.service_id }
						: item
				);
			});
		},
		[allowedCurrencies, defaultCurrency, generalMasterList, t]
	);

	const selectInvestigationForRow = useCallback(
		(rowId, testId) => {
			if (!testId) {
				setInvestigationItems((prev) =>
					prev.map((item) =>
						item.id === rowId
							? {
									...item,
									test_id: "",
									test_name: "",
									quantity: 1,
									unit_price: 0,
									amount: 0,
									isNewRow: true,
								}
							: item
					)
				);
				return;
			}

			const master = labMasterList.find(
				(item) => String(item.investigation_id || item._id) === String(testId)
			);
			if (!master) return;

			const candidate = investigationFromMaster(
				master,
				defaultCurrency,
				allowedCurrencies
			);

			setInvestigationItems((prev) => {
				const duplicate = prev.some(
					(item) =>
						item.id !== rowId && String(item.test_id) === String(candidate.test_id)
				);
				if (duplicate) {
					toast.info(t("Investigation already added."));
					return prev;
				}
				return prev.map((item) =>
					item.id === rowId
						? { ...item, ...candidate, id: item.id, isNewRow: !candidate.test_id }
						: item
				);
			});
		},
		[allowedCurrencies, defaultCurrency, labMasterList, t]
	);

	const updateServicesDiscount = useCallback((patch) => {
		if (patch.discountType !== undefined) setServicesDiscountType(patch.discountType);
		if (patch.discountValue !== undefined) {
			setServicesDiscountValue(toMoneyNumber(patch.discountValue));
		}
	}, []);

	const updateInvestigationsDiscount = useCallback((patch) => {
		if (patch.discountType !== undefined) setInvestigationsDiscountType(patch.discountType);
		if (patch.discountValue !== undefined) {
			setInvestigationsDiscountValue(toMoneyNumber(patch.discountValue));
		}
	}, []);

	const updateServicesTaxConfig = useCallback((patch) => {
		setServicesTaxConfig((prev) => {
			let next = { ...prev, ...patch };
			if (patch.taxPercentage !== undefined) {
				next.taxPercentage = clampTaxPercent(patch.taxPercentage);
			}
			if (patch.taxType !== undefined) {
				next.taxType = String(patch.taxType).toLowerCase();
			}
			if (next.taxType !== "vat") {
				next = syncGstTaxConfig(next);
			}
			return next;
		});
	}, []);

	const updateInvestigationsTaxConfig = useCallback((patch) => {
		setInvestigationsTaxConfig((prev) => {
			let next = { ...prev, ...patch };
			if (patch.taxPercentage !== undefined) {
				next.taxPercentage = clampTaxPercent(patch.taxPercentage);
			}
			if (patch.taxType !== undefined) {
				next.taxType = String(patch.taxType).toLowerCase();
			}
			if (next.taxType !== "vat") {
				next = syncGstTaxConfig(next);
			}
			return next;
		});
	}, []);

	const buildCollectPaymentPayload = useCallback(
		({ markAsPaid = false, paidAmount = 0, currency, paymentMode } = {}) => {
			const payloadCurrency =
				getBillingBaseCurrency(invoice, defaultCurrency) || defaultCurrency;
			const showLabSection = isTreatmentPackage || billingFlags.showLabSection;

			const servicesLineFields = buildSectionLineBillingFieldsMap({
				items: serviceItems,
				discountType: servicesDiscountType,
				discountValue: servicesDiscountValue,
				taxConfig: getEffectiveSectionTaxConfig(
					servicesTaxConfig,
					billingFlags.showServicesTax,
					posTaxType
				),
				isSameState,
				posTaxType,
				targetCurrency: payloadCurrency,
				convertCurrency,
				pharmacyCurrency: defaultCurrency,
				allowedCurrencies,
			});

			const investigationsLineFields = buildSectionLineBillingFieldsMap({
				items: showLabSection ? investigationItems : [],
				discountType: investigationsDiscountType,
				discountValue: investigationsDiscountValue,
				taxConfig: getEffectiveSectionTaxConfig(
					investigationsTaxConfig,
					isTreatmentPackage || billingFlags.showLabTax,
					posTaxType
				),
				isSameState,
				posTaxType,
				targetCurrency: payloadCurrency,
				convertCurrency,
				pharmacyCurrency: defaultCurrency,
				allowedCurrencies,
			});

			const consultation = serviceItems
				.filter((item) => item.isConsultation)
				.map((item) => ({
					consultation_name: item.service_name,
					doctor: invoice?.doctor?.name || "",
					quantity: Math.max(1, Number(item.quantity) || 1),
					price: convertLineAmount(
						getServiceUnitPrice(item),
						item.currency,
						payloadCurrency,
						convertCurrency,
						defaultCurrency,
						allowedCurrencies
					),
					amount: toMoneyNumber(item.amount),
					currency: payloadCurrency,
					...(servicesLineFields.get(item.id) || {}),
				}));

			const general_services = serviceItems
				.filter((item) => !item.isConsultation)
				.map((item) => ({
					service_id: item.service_id,
					service_name: item.service_name,
					quantity: Math.max(1, Number(item.quantity) || 1),
					price: convertLineAmount(
						getServiceUnitPrice(item),
						item.currency,
						payloadCurrency,
						convertCurrency,
						defaultCurrency,
						allowedCurrencies
					),
					amount: toMoneyNumber(item.amount),
					currency: payloadCurrency,
					source: item.source,
					...(servicesLineFields.get(item.id) || {}),
				}));

			const paidAmountInPayment = toMoneyNumber(paidAmount);
			const paidAmountBase =
				resolvedPaymentCurrency === billingCurrency
					? paidAmountInPayment
					: convertBillingAmount(
							paidAmountInPayment,
							resolvedPaymentCurrency,
							billingCurrency,
							convertCurrency,
							defaultCurrency,
							allowedCurrencies
						);

			const payloadPharmacyItems = withPharmacyItemSaleDate(pharmacyItems);

			const basePayload = {
				...buildBillingContextFields({
					prescription: invoice,
					selectedPatient: invoice?.patient,
					posTaxType,
				}),
				consultation,
				general_services,
				investigations: (showLabSection ? investigationItems : []).map((item) => ({
					test_id: item.test_id,
					test_name: item.test_name,
					type: item.type,
					source: item.source,
					quantity: Math.max(1, Number(item.quantity) || 1),
					price: convertLineAmount(
						getInvestigationUnitPrice(item),
						item.currency,
						payloadCurrency,
						convertCurrency,
						defaultCurrency,
						allowedCurrencies
					),
					amount: toMoneyNumber(item.amount),
					currency: payloadCurrency,
					...(investigationsLineFields.get(item.id) || {}),
				})),
				pharmacy_items: payloadPharmacyItems,
				summary: billingSummary,
				payment: {
					currency: currency || resolvedPaymentCurrency,
					payment_method: paymentMode || "cash",
					paid_amount: paidAmountInPayment,
					amount_received: paidAmountInPayment,
					pending_amount: markAsPaid
						? 0
						: Math.max(0, toMoneyNumber(dueAmount) - paidAmountBase),
					grand_total: grandTotal,
					mark_as_paid: markAsPaid,
				},
			};

			if (!isTreatmentPackage) return basePayload;

			const treatmentExtras = buildTreatmentBillingPayloadExtras({
				prescription: invoice,
				serviceItems,
				pharmacyItems: payloadPharmacyItems,
				summary: basePayload.summary,
				paymentInfo: basePayload.payment,
			});

			return {
				...basePayload,
				...treatmentExtras,
				consultation: basePayload.consultation,
				general_services: basePayload.general_services,
				investigations: basePayload.investigations,
				pharmacy_items: basePayload.pharmacy_items,
				summary: treatmentExtras.summary,
				payment: basePayload.payment,
				prescription_id: invoice?._id || null,
			};
		},
		[
			allowedCurrencies,
			billingCurrency,
			billingSummary,
			convertCurrency,
			defaultCurrency,
			dueAmount,
			grandTotal,
			billingFlags,
			invoice,
			investigationItems,
			investigationsDiscountType,
			investigationsDiscountValue,
			investigationsTaxConfig,
			isSameState,
			isTreatmentPackage,
			pharmacyItems,
			posTaxType,
			resolvedPaymentCurrency,
			serviceItems,
			servicesDiscountType,
			servicesDiscountValue,
			servicesTaxConfig,
		]
	);

	return {
		loading,
		normalizedInvoice,
		billingFlags,
		isTreatmentPackage,
		treatmentDetails,
		treatmentPaymentHistory,
		packagePrice,
		baseCurrency,
		billingCurrency,
		displayCurrency,
		paymentCurrency: resolvedPaymentCurrency,
		defaultCurrency,
		pharmacyCurrency,
		posTaxType,
		pharmacyStateCode,
		patientStateCode,
		serviceItems,
		investigationItems,
		pharmacyItems,
		servicesBillingTotals,
		investigationsBillingTotals,
		billingSummary,
		servicesDiscountType,
		servicesDiscountValue,
		servicesTaxConfig,
		investigationsDiscountType,
		investigationsDiscountValue,
		investigationsTaxConfig,
		generalMasterList,
		labMasterList,
		sectionOpen,
		paidSoFar,
		grandTotal,
		dueAmount,
		dueAmountPayment,
		toggleSection,
		updateServiceAmount,
		updateServiceQuantity,
		updateInvestigationAmount,
		updateInvestigationQuantity,
		removeServiceItem,
		removeInvestigation,
		addServiceRow,
		addInvestigationRow,
		selectServiceForRow,
		selectInvestigationForRow,
		updateServicesDiscount,
		updateInvestigationsDiscount,
		updateServicesTaxConfig,
		updateInvestigationsTaxConfig,
		buildCollectPaymentPayload,
	};
};

export default useCollectPaymentBilling;
